import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Upload, FileText, FileImage, FileSpreadsheet, File as FileIcon, Trash2, Download, Eye, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "royalty_statement", label: "Royalty Statement" },
  { id: "contract", label: "Contract" },
  { id: "split_sheet", label: "Split Sheet" },
  { id: "id_verification", label: "ID / Verification" },
  { id: "other", label: "Other" },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.label]));

function fileIconFor(ext) {
  if (!ext) return FileIcon;
  if ([".png", ".jpg", ".jpeg", ".webp"].includes(ext)) return FileImage;
  if ([".csv", ".xlsx"].includes(ext)) return FileSpreadsheet;
  return FileText;
}

function bytesPretty(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Documents vault component.
 * Props:
 *   - listUrl: GET endpoint
 *   - uploadUrl: POST endpoint (multipart: file, title, category)
 *   - deleteUrl: (docId) => DELETE endpoint OR null to hide delete (when admin viewing artist's docs)
 *   - downloadUrl: (docId) => GET binary endpoint
 *   - heading: section title (optional)
 *   - canUpload: bool (default true)
 */
export default function DocumentsVault({
  listUrl,
  uploadUrl,
  deleteUrl,
  downloadUrl,
  heading = "Document vault",
  canUpload = true,
}) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("royalty_statement");
  const [preview, setPreview] = useState(null); // { id, url, kind, original_name, title, ext }

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(listUrl);
      setDocs(data);
    } catch (e) {
      console.error("Failed to load documents", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listUrl]);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!title.trim()) {
      toast.error("Add a document title first.");
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title.trim());
    fd.append("category", category);
    setUploading(true);
    try {
      await api.post(uploadUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      setTitle("");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(downloadUrl(doc.id), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_name || doc.stored_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const remove = async (doc) => {
    if (!deleteUrl) return;
    if (!window.confirm(`Delete "${doc.title}"? This can't be undone.`)) return;
    try {
      await api.delete(deleteUrl(doc.id));
      toast.success("Deleted");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const isPreviewable = (ext) => {
    if (!ext) return false;
    return [".pdf", ".png", ".jpg", ".jpeg", ".webp", ".txt", ".md", ".csv"].includes(ext);
  };

  const kindFor = (ext) => {
    if (ext === ".pdf") return "pdf";
    if ([".png", ".jpg", ".jpeg", ".webp"].includes(ext)) return "image";
    return "text";
  };

  const openPreview = async (doc) => {
    if (!isPreviewable(doc.ext)) {
      toast.message("This file type can't be previewed — downloading instead.");
      return download(doc);
    }
    try {
      const res = await api.get(`/me/documents/${doc.id}/view`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      let textContent = null;
      const kind = kindFor(doc.ext);
      if (kind === "text") {
        textContent = await res.data.text();
      }
      setPreview({
        id: doc.id,
        url: blobUrl,
        kind,
        ext: doc.ext,
        original_name: doc.original_name,
        title: doc.title,
        size: doc.size,
        textContent,
      });
    } catch (e) {
      toast.error("Preview failed");
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  return (
    <section className="bg-[#0a0a0a] border border-white/10">
      <div className="p-6 md:p-8 border-b border-white/10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              // vault
            </div>
            <h3 className="font-heading font-bold text-xl mt-2">{heading}</h3>
          </div>
          <div className="text-xs text-zinc-500">{docs.length} file{docs.length === 1 ? "" : "s"}</div>
        </div>

        {canUpload && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-5">
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                Title
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Q3 2025 royalty statement"
                className="input-gold"
                data-testid="doc-title"
              />
            </div>
            <div className="md:col-span-4">
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="input-gold"
                data-testid="doc-category"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="inline-flex items-center justify-center w-full btn-primary cursor-pointer">
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.docx,.txt,.md"
                  onChange={onFileChange}
                  disabled={uploading}
                  data-testid="doc-file-input"
                />
                <Upload size={14} /> {uploading ? "Uploading…" : "Upload file"}
              </label>
            </div>
            <div className="md:col-span-12 text-[10px] text-zinc-600 font-mono">
              PDF · DOCX · XLSX · CSV · PNG · JPG · WEBP · TXT — max 15 MB
            </div>
          </div>
        )}
      </div>

      <div className="divide-y divide-white/5">
        {loading && (
          <div className="p-6 text-sm text-zinc-600">Loading…</div>
        )}
        {!loading && docs.length === 0 && (
          <div className="p-10 text-center text-zinc-600 text-sm">No documents yet.</div>
        )}
        {docs.map((d) => {
          const Icon = fileIconFor(d.ext);
          return (
            <div key={d.id} className="p-4 md:p-6 flex items-center gap-4 hover:bg-white/[0.02]">
              <div className="w-10 h-10 border border-white/10 flex items-center justify-center text-[var(--primary)]">
                <Icon size={18} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{d.title}</div>
                <div className="text-xs text-zinc-500 font-mono flex items-center gap-3 mt-1">
                  <span className="text-[var(--primary)]">{CATEGORY_LABEL[d.category] || d.category}</span>
                  <span>·</span>
                  <span className="truncate">{d.original_name}</span>
                  <span>·</span>
                  <span>{bytesPretty(d.size)}</span>
                  {d.uploaded_by_role === "admin" && (
                    <>
                      <span>·</span>
                      <span className="text-amber-300">uploaded by consultant</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isPreviewable(d.ext) && (
                  <button
                    onClick={() => openPreview(d)}
                    className="text-zinc-400 hover:text-[var(--primary)] p-2"
                    data-testid={`doc-preview-${d.id}`}
                    title="Preview"
                  >
                    <Eye size={16} />
                  </button>
                )}
                <button
                  onClick={() => download(d)}
                  className="text-zinc-400 hover:text-[var(--primary)] p-2"
                  data-testid={`doc-download-${d.id}`}
                  title="Download"
                >
                  <Download size={16} />
                </button>
                {deleteUrl && (
                  <button
                    onClick={() => remove(d)}
                    className="text-zinc-500 hover:text-red-400 p-2"
                    data-testid={`doc-delete-${d.id}`}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 md:p-8"
          onClick={(e) => { if (e.target === e.currentTarget) closePreview(); }}
          data-testid="doc-preview-modal"
        >
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-5xl max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-white/10 gap-4">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--primary)]">
                  // preview
                </div>
                <h3 className="font-heading font-bold text-lg md:text-xl truncate mt-1">{preview.title}</h3>
                <div className="text-xs text-zinc-500 font-mono truncate">{preview.original_name} · {bytesPretty(preview.size || 0)}</div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={preview.url}
                  download={preview.original_name}
                  className="text-zinc-400 hover:text-[var(--primary)] p-2"
                  title="Download"
                  data-testid="doc-preview-download"
                >
                  <Download size={18} />
                </a>
                <button
                  onClick={closePreview}
                  className="text-zinc-400 hover:text-white p-2"
                  data-testid="doc-preview-close"
                  title="Close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-[#020202] flex items-center justify-center">
              {preview.kind === "pdf" && (
                <iframe
                  src={preview.url}
                  title={preview.title}
                  className="w-full h-[78vh] bg-white"
                  data-testid="doc-preview-pdf"
                />
              )}
              {preview.kind === "image" && (
                <img
                  src={preview.url}
                  alt={preview.title}
                  className="max-h-[78vh] max-w-full object-contain"
                  data-testid="doc-preview-image"
                />
              )}
              {preview.kind === "text" && (
                <pre
                  className="w-full h-[78vh] overflow-auto p-6 text-sm text-zinc-200 whitespace-pre-wrap font-mono leading-relaxed"
                  data-testid="doc-preview-text"
                >
                  {preview.textContent}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
